import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { supabase } from '../../../lib/supabase';

interface DiscountCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  duration_type: string;
  duration_months: number | null;
  max_redemptions: number | null;
  expires_at: string | null;
  created_at: string | null;
  is_active: boolean;
}

interface DiscountCodeFormProps {
  companyId: string;
  discountCode: DiscountCode | null;
  onSave: (code: DiscountCode) => void;
  onCancel: () => void;
}

export function DiscountCodeForm({ companyId, discountCode, onSave, onCancel }: DiscountCodeFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: discountCode?.code || '',
    description: discountCode?.description || '',
    discountType: discountCode?.discount_type || 'percent',
    discountValue: discountCode?.discount_value?.toString() || '',
    duration: discountCode?.duration_type || 'forever',
    durationMonths: discountCode?.duration_months?.toString() || '',
    maxRedemptions: discountCode?.max_redemptions?.toString() || '',
    expiresAt: discountCode?.expires_at 
      ? new Date(discountCode.expires_at).toISOString().split('T')[0] 
      : '',
  });

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, code });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }
    if (!form.discountValue) {
      setError('Discount value is required');
      return;
    }

    setSaving(true);
    setError(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {
      company_id: companyId,
      code: form.code.trim().toUpperCase(),
      description: form.description.trim() || null,
      discount_type: form.discountType,
      discount_value: parseFloat(form.discountValue) || 0,
      duration_type: form.duration,
      duration_months: form.duration === 'repeating' ? parseInt(form.durationMonths) || null : null,
      max_redemptions: form.maxRedemptions ? parseInt(form.maxRedemptions) : null,
      expires_at: form.expiresAt || null,
      is_active: true,
    };

    try {
      if (discountCode) {
        // Update
        const { data: updated, error: updateError } = await supabase
          .from('discount_codes')
          .update(data)
          .eq('id', discountCode.id)
          .select()
          .single();

        if (updateError) throw updateError;
        onSave(updated);
      } else {
        // Create
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: created, error: createError } = await (supabase.from as any)('discount_codes')
          .insert(data)
          .select()
          .single();

        if (createError) throw createError;
        onSave(created);
      }
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      if (error.code === '23505') {
        setError('This code already exists');
      } else {
        setError(error.message || 'Failed to save discount code');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#EBFFEE]">
          {discountCode ? 'Edit Discount Code' : 'Create Discount Code'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 max-h-[60vh] overflow-y-auto">
        {/* Code */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Code <span className="text-rollops-accent">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g., WELCOME20"
              className="flex-1 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] font-mono uppercase placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              required
            />
            <button
              type="button"
              onClick={generateCode}
              className="px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-[#A9A3A3] rounded-lg transition-colors text-sm"
            >
              Generate
            </button>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Description
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="e.g., New member discount"
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
          />
        </div>

        {/* Discount Type */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Discount Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, discountType: 'percent' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.discountType === 'percent'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Percentage (%)
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, discountType: 'fixed' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.discountType === 'fixed'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Fixed Amount ($)
            </button>
          </div>
        </div>

        {/* Discount Value */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Discount Value <span className="text-rollops-accent">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">
              {form.discountType === 'fixed' ? '$' : ''}
            </span>
            <input
              type="number"
              step={form.discountType === 'percent' ? '1' : '0.01'}
              max={form.discountType === 'percent' ? '100' : undefined}
              value={form.discountValue}
              onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
              placeholder={form.discountType === 'percent' ? '20' : '10.00'}
              className={`w-full py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 ${
                form.discountType === 'fixed' ? 'pl-8 pr-4' : 'px-4'
              }`}
              required
            />
            {form.discountType === 'percent' && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">%</span>
            )}
          </div>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Duration
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, duration: 'forever' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.duration === 'forever'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Forever
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, duration: 'repeating' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.duration === 'repeating'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Repeating
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, duration: 'once' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.duration === 'once'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Once
            </button>
          </div>
          {form.duration === 'repeating' && (
            <input
              type="number"
              min="1"
              value={form.durationMonths}
              onChange={(e) => setForm({ ...form, durationMonths: e.target.value })}
              placeholder="Number of months"
              className="w-full mt-2 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          )}
        </div>

        {/* Max Redemptions */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Max Redemptions (optional)
          </label>
          <input
            type="number"
            min="1"
            value={form.maxRedemptions}
            onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
            placeholder="Leave empty for unlimited"
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
          />
        </div>

        {/* Expiration Date */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Expiration Date (optional)
          </label>
          <input
            type="date"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
          />
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              discountCode ? 'Update' : 'Create'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
